import { useSuspenseQuery } from '@tanstack/react-query'

import { getRequest } from 'src/lib/api-client'
import { users } from 'src/shared/features/users/queries/user-queries'
import type {
  CreditScore,
  ClientType,
  LoanType,
  PropertyType,
  TitleTransactionType,
  AppraiserTransactionType,
  ProjectType,
  AttorneyTransactionType,
} from 'src/shared/schemas/referrals'

export type TransactionType = 'created' | 'received'

export type Transaction = {
  updatedAt: string
  propertyCity: string
  propertyState: string
  propertyType: PropertyType
  bathrooms: number | null
  bedrooms: number | null
  budget: { min: number; max: number } | null
  clientType: ClientType | null
  creditScoreEstimate: CreditScore | null
  downPaymentEstimate: number | null
  estimatedPropertyValue: number | null
  exactPrice: number | null
  loanType: LoanType | null
  priceRange: { min: number; max: number } | null
  purchasePrice: number | null
  titleTransactionType: TitleTransactionType | null
  appraisalPurpose: AppraiserTransactionType | null
  projectType: ProjectType | null
  attorneyTransactionType: AttorneyTransactionType | null
  coverage: string[] | null
}

type TransactionResponse = {
  transactions: Transaction[]
  totalVolume: {
    min: number
    max: number
  }
}

const getUserTransactions = async (userId: number, filter?: TransactionType): Promise<TransactionResponse> => {
  const response = await getRequest<TransactionResponse>(
    `/users/transactions?user_id=${userId}${filter ? `&filter=${filter}` : ''}`,
  )
  if (!response) {
    throw new Error('Failed to fetch transactions')
  }
  return response
}

export const useUserTransactions = (userId: number, filter?: TransactionType) => {
  return useSuspenseQuery({
    queryKey: users.transactions(userId, filter).queryKey,
    queryFn: () => getUserTransactions(userId, filter),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
